import { Textfield } from '@digdir/designsystemet-react';
import type { ChangeEvent, ReactNode } from 'react';

import styled from '../../../../../../styled';
import { parseNorwegianFormattedNumber } from '../../../../../../utils/utils';
import { useSolcelleFormContext } from '../utils';

type SolarAmountFieldName = `kwpPanels.${number}.effect` | `sqmPanels.${number}.area`;

type SolarPanelAmountInputProps = {
  error?: ReactNode;
  label: string;
  name: SolarAmountFieldName;
  pattern: {
    message: string;
    value: RegExp;
  };
  sliderMax: number;
  sliderMin: number;
  sliderStep?: number;
  suffix: string;
  validate: (value?: number) => string | true;
  value?: number | string;
};

const parseSliderValue = (value: number | string | undefined) => {
  if (value === undefined || value === '') {
    return undefined;
  }

  const parsedValue = typeof value === 'number' ? value : Number(value.replace(',', '.'));
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Maps linear slider position to illustration fill; exponent < 1 yields quicker visual gain at low values, slower near max. */
const ILLUSTRATION_FILL_CURVE_EXPONENT = 0.63;

export const SolarPanelAmountInput = ({
  error,
  label,
  name,
  pattern,
  sliderMax,
  sliderMin,
  sliderStep = 1,
  suffix,
  validate,
  value
}: SolarPanelAmountInputProps) => {
  const { register, setValue } = useSolcelleFormContext();
  const parsedValue = parseSliderValue(value);
  const sliderValue = clamp(parsedValue ?? sliderMin, sliderMin, sliderMax);

  const updateFromSlider = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(name, Number(event.currentTarget.value), {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  return (
    <AmountField>
      <Textfield
        type="text"
        inputMode="decimal"
        suffix={suffix}
        label={label}
        error={error}
        {...register(name, {
          setValueAs: (fieldValue) => parseNorwegianFormattedNumber(fieldValue),
          validate,
          pattern
        })}
      />
      <Slider
        type="range"
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        value={sliderValue}
        onChange={updateFromSlider}
        aria-label={`${label}, ${sliderValue} ${suffix}`}
      />
      <IllustrationFrame aria-hidden="true">
        <PanelArrayIllustration value={sliderValue} min={sliderMin} max={sliderMax} />
      </IllustrationFrame>
    </AmountField>
  );
};

const PanelArrayIllustration = ({ value, min, max }: { max: number; min: number; value: number }) => {
  const linearNormalized = max === min ? 1 : (value - min) / (max - min);
  const t = clamp(linearNormalized, 0, 1);
  const curvedNormalized = Math.pow(t, ILLUSTRATION_FILL_CURVE_EXPONENT);
  const activePanelCount = Math.max(1, Math.ceil(curvedNormalized * 12));

  return (
    <Svg viewBox="0 0 120 120" role="img" aria-hidden="true">
      <line className="panel-rack" x1="22" y1="94" x2="98" y2="94" />
      {Array.from({ length: 12 }).map((_, index) => {
        const column = index % 4;
        const row = Math.floor(index / 4);
        const x = 24 + column * 18;
        const y = 70 - row * 18;
        const isActive = index < activePanelCount;

        return (
          <g className={isActive ? undefined : 'panel-slot'} key={index}>
            <rect x={x} y={y} width="15" height="12" rx="1" fill="var(--ds-color-base-default)" stroke="currentColor" strokeWidth="2" />
            <line className="panel-detail" x1={x + 7.5} y1={y} x2={x + 7.5} y2={y + 12} />
            <line className="panel-detail" x1={x} y1={y + 6} x2={x + 15} y2={y + 6} />
          </g>
        );
      })}
      <line className="panel-rack" x1="60" y1="82" x2="60" y2="94" />
    </Svg>
  );
};

const AmountField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-size-2);
`;

const Slider = styled.input`
  accent-color: var(--ds-color-base-default);
  inline-size: 100%;
  margin: 0;
  touch-action: pan-y;
`;

const Svg = styled.svg`
  color: var(--ds-color-text-default);
  block-size: 7rem;
  inline-size: auto;
  max-width: 8rem;

  .panel-detail {
    stroke: var(--ds-color-background-default);
    stroke-width: 1.5;
  }

  .panel-rack {
    stroke: currentColor;
    stroke-linecap: round;
    stroke-width: 3;
  }

  .panel-slot {
    opacity: 0.22;
  }
`;

const IllustrationFrame = styled.div`
  align-items: center;
  background: var(--ds-color-background-default);
  block-size: 8.5rem;
  border: 1px solid var(--ds-color-border-subtle);
  border-radius: var(--ds-border-radius-md);
  display: flex;
  justify-content: center;
  padding: var(--ds-size-2);
`;
